import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { WishlistProvider } from './context/WishlistContext';
import { ToastProvider } from './context/ToastContext';
import Header from './components/Header';
import Footer from './components/Footer';
import PrivateRoute from './components/PrivateRoute';
import ProtectedRoute from './components/ProtectedRoute';
import './App.css';

// Route-level code splitting: each page ships in its own chunk and is only
// downloaded when first visited. This keeps the initial (landing) bundle small
// — the heavy admin dashboard, checkout, etc. no longer load for every visitor.
const Home = lazy(() => import('./pages/Home'));
const Shop = lazy(() => import('./pages/Shop'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Login = lazy(() => import('./pages/Login'));
const Account = lazy(() => import('./pages/Account'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const Contact = lazy(() => import('./pages/Contact'));
const About = lazy(() => import('./pages/About'));
const Services = lazy(() => import('./pages/Services'));
const ServiceDetail = lazy(() => import('./pages/ServiceDetail'));
const Orders = lazy(() => import('./pages/Orders'));
const Wishlist = lazy(() => import('./pages/Wishlist'));
const Brands = lazy(() => import('./pages/Brands'));
const Categories = lazy(() => import('./pages/Categories'));
const OrderConfirmation = lazy(() => import('./pages/OrderConfirmation'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));

// Admin (heavy — kept out of the public bundle entirely)
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const OrdersManagement = lazy(() => import('./pages/OrdersManagement'));
const InventoryManagement = lazy(() => import('./pages/InventoryManagement'));
const AccountsManagement = lazy(() => import('./pages/AccountsManagement'));
const HRManagement = lazy(() => import('./pages/HRManagement'));
const AdminImageLibrary = lazy(() => import('./pages/AdminImageLibrary'));
const AdminOffers = lazy(() => import('./pages/AdminOffers'));
const AdminTeam = lazy(() => import('./pages/AdminTeam'));
const AdminHeroSettings = lazy(() => import('./pages/AdminHeroSettings'));
const AdminSiteContent = lazy(() => import('./pages/AdminSiteContent'));
const AdminWhatsApp = lazy(() => import('./pages/AdminWhatsApp'));

// Lightweight fallback shown while a route chunk downloads.
const RouteFallback = () => (
  <div className="route-loading" aria-busy="true" aria-label="Loading">
    <div className="route-loading-spinner" />
  </div>
);

function App() {
  return (
    <Router>
      <AuthProvider>
        <ToastProvider>
        <CartProvider>
        <WishlistProvider>
          <div className="app-container">
            <Header />
            <main className="main-content">
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<Home />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/services/:slug" element={<ServiceDetail />} />
                  <Route path="/shop" element={<Shop />} />
                  <Route path="/product/:id" element={<ProductDetail />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Navigate to="/login" state={{ register: true }} replace />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/categories" element={<Categories />} />
                  <Route path="/brands" element={<Brands />} />
                  <Route path="/order-confirmation/:orderId" element={<OrderConfirmation />} />
                  {/* Wishlist works for guests too — it lives in localStorage */}
                  <Route path="/wishlist" element={<Wishlist />} />

                  {/* Protected Routes (User) */}
                  <Route
                    path="/account"
                    element={
                      <PrivateRoute>
                        <Account />
                      </PrivateRoute>
                    }
                  />
                  {/* Cart & checkout are open to guests (in-page sign-in prompt) */}
                  <Route path="/cart" element={<Cart />} />
                  <Route path="/checkout" element={<Checkout />} />
                  {/* Open to guests: the page shows account orders when signed
                      in, and orders placed from this device otherwise */}
                  <Route path="/orders" element={<Orders />} />

                  {/* Admin Routes */}
                  <Route
                    path="/admin/*"
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin-dashboard"
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/orders"
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <OrdersManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/inventory"
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <InventoryManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/accounts"
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <AccountsManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/hr"
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <HRManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/image-library"
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminImageLibrary />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/offers"
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminOffers />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/team"
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminTeam />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/hero-settings"
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminHeroSettings />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/site-content"
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminSiteContent />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/whatsapp"
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminWhatsApp />
                      </ProtectedRoute>
                    }
                  />

                  {/* 404 Page */}
                  <Route path="*" element={
                    <div className="not-found-container">
                      <h1>404 - Page Not Found</h1>
                      <p>The page you're looking for doesn't exist.</p>
                      <a href="/" className="btn btn-primary">Return to Home</a>
                    </div>
                  } />
                </Routes>
              </Suspense>
            </main>
            <Footer />
          </div>
        </WishlistProvider>
        </CartProvider>
        </ToastProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
